export const POLICY_BUNDLE_VERSION = "2026-06-23";

export const MARKETPLACE_PROTECTION_TEXT = `Bonsai Broker Marketplace Protection Policy

Effective Date: June 23, 2026

1. Purpose

Bonsai Broker is an online marketplace that connects independent buyers and sellers of bonsai trees, pre-bonsai, pots, tools, supplies, and related items.

This Marketplace Protection Policy explains how Bonsai Broker handles disputes, account investigations, and marketplace integrity. It also clarifies the responsibilities of buyers, sellers, and Bonsai Broker.

By using Bonsai Broker, you agree to this policy.

2. Our Role

Bonsai Broker is not the owner, manufacturer, importer, exporter, distributor, or seller of products listed on the platform.

Except where required by applicable law, Bonsai Broker:

Does not inspect listed items.
Does not guarantee the accuracy of listings.
Does not guarantee delivery.
Does not guarantee product quality or health.
Does not guarantee the identity of buyers or sellers.
Does not guarantee the outcome of any transaction.

Each transaction is entered into solely between the buyer and the seller.

3. Buyer Protection

While Bonsai Broker is not responsible for transactions between users, we strive to maintain a trustworthy marketplace.

If a problem occurs, buyers should first contact the seller directly through Bonsai Broker's messaging system.

Examples include:

Item never shipped
Wrong item received
Major shipping damage
Missing parts
Item significantly different than described

Many issues can be resolved through direct communication.

4. Marketplace Assistance

If a buyer and seller cannot reach a reasonable resolution, Bonsai Broker may, at its sole discretion:

Review transaction records.
Review messages exchanged through Bonsai Broker.
Request additional photographs.
Request shipping documentation.
Request tracking information.
Request proof of delivery.
Request additional evidence from either party.

Participation in this review process does not create any obligation for Bonsai Broker to issue a refund or resolve the dispute in favor of either party.

5. Seller Protection

Bonsai Broker also seeks to protect honest sellers.

If a buyer files a complaint, sellers may submit:

Tracking numbers
Shipping receipts
Carrier documentation
Packaging photographs
Product photographs taken before shipment
Message history
Any other relevant evidence

False or fraudulent claims made against sellers may result in account action against the buyer.

6. Live Plant Disclaimer

Live trees are living organisms and naturally vary in appearance and condition.

Buyers acknowledge that:

Seasonal color changes are normal.
Leaf drop may occur during shipping.
Dormancy is not considered damage.
Minor branch movement or soil disturbance during shipping may occur.
Trees continue to grow and change after listing photographs are taken.

These natural characteristics do not necessarily constitute a product defect.

7. Shipping Risks

Shipping carriers may occasionally experience:

Delays
Weather interruptions
Mishandling
Lost packages

Bonsai Broker is not responsible for shipping carrier performance.

Claims relating to carrier negligence should be submitted directly to the shipping carrier whenever applicable.

8. Prohibited Conduct

Marketplace Protection does not apply where either party has violated Bonsai Broker policies, including but not limited to:

Fraud
False listings
Fake tracking numbers
Chargeback abuse
Review manipulation
Selling prohibited items
Off-platform payment arrangements intended to avoid marketplace fees
Harassment or threats

Violations may result in immediate suspension or permanent removal from the platform.

9. Payment Disputes

If a buyer initiates a payment dispute or chargeback through their financial institution, Bonsai Broker may provide relevant transaction records, messages, and shipping information to the payment processor or financial institution.

Fraudulent or abusive chargebacks may result in permanent account suspension.

10. Account Investigations

Bonsai Broker reserves the right to investigate any transaction when fraud, abuse, policy violations, or illegal activity is suspected.

During an investigation, Bonsai Broker may:

Temporarily remove listings.
Restrict account functionality.
Suspend accounts.
Request identity verification.
Request proof of ownership.
Request additional documentation.

Failure to cooperate may result in account termination.

11. No Guarantee of Recovery

While Bonsai Broker may review disputes and assist users where appropriate, we do not guarantee that:

Lost funds will be recovered.
A refund will be issued.
Replacement products will be provided.
Disputes will be resolved in favor of either party.

Any action taken by Bonsai Broker is voluntary unless otherwise required by law.

12. Marketplace Integrity

To promote a safe and trusted marketplace, Bonsai Broker may use automated systems and manual review to detect:

Fraudulent listings
Fake accounts
Unusual transaction patterns
Review manipulation
Spam
Policy violations

Accounts may be reviewed without prior notice where necessary to protect the marketplace.

13. Limitation of Liability

To the fullest extent permitted by applicable law, Bonsai Broker shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to transactions between buyers and sellers.

This includes, but is not limited to:

Loss of profits
Lost business opportunities
Damage to reputation
Emotional distress
Loss of collectible value
Shipping delays
Plant loss or decline after delivery
Carrier mishandling

Any liability of Bonsai Broker shall be limited to the maximum extent permitted by law.

14. Policy Updates

Bonsai Broker reserves the right to modify this Marketplace Protection Policy at any time. Updated versions will become effective upon publication on the website. Continued use of the marketplace after changes are posted constitutes acceptance of the revised policy.`;

export const COMMUNITY_STANDARDS_TEXT = `Bonsai Broker Community Standards

Bonsai Broker exists to support the bonsai community.

We expect members to treat one another respectfully.

Respect

No:

Harassment
Bullying
Threats
Hate speech
Discrimination

Honesty

Members must:

Describe items honestly
Disclose defects
Use real photographs

Authenticity

Do not:

Create fake accounts
Manipulate reviews
Impersonate others

Marketplace Integrity

Do not:

Circumvent fees
Spam users
Send unsolicited advertisements

Privacy

Respect the privacy of other members.

Do not publish another user's personal information without permission.

Illegal Activity

Do not use Bonsai Broker for unlawful purposes.

Enforcement

Violations may result in:

Content removal
Listing removal
Warning
Temporary suspension
Permanent account termination

Bonsai Broker reserves the right to investigate violations and take appropriate action at its sole discretion.`;

export const DMCA_POLICY_TEXT = `DMCA / Copyright Policy

Bonsai Broker respects intellectual property rights.

If you believe copyrighted material has been uploaded without permission, submit a written DMCA notice containing:

Your name
Contact information
Description of copyrighted work
URL of infringing content
Statement under penalty of perjury
Electronic signature

Upon receipt of a valid notice, Bonsai Broker may:

Remove the content
Disable access
Notify the uploader

Users who repeatedly violate copyright rights may have their accounts permanently terminated.

Counter-notifications may be submitted as provided under the Digital Millennium Copyright Act.`;

export const SELLER_RULES_TEXT = `Bonsai Broker Seller Rules

Effective Date: June 23, 2026

Welcome to Bonsai Broker. These Seller Rules govern all listings and sales conducted through the Bonsai Broker marketplace. By listing an item for sale, you agree to comply with these rules.

1. Seller Responsibility

Sellers are solely responsible for:

The accuracy of every listing
The condition of the item
Proper packaging
Shipping the purchased item
Customer communication
Compliance with all applicable laws

Bonsai Broker does not own, inspect, or verify listed items.

2. Accurate Listings

Listings must include:

Current photographs of the actual item
Accurate measurements
Species (when applicable)
Honest condition
Known defects
Price
Shipping details

Stock photographs may not be used unless clearly identified.

3. Communication

Sellers must:

Respond promptly
Remain professional
Avoid harassment
Never threaten buyers
4. Shipping Time

Unless otherwise stated:

Orders should ship within 5 business days.

If delays occur, buyers should be notified immediately.

5. Packaging

Live trees must be packaged appropriately for transport.

Failure to properly package live material may result in account action.

6. Pricing

Price manipulation is prohibited.

This includes:

Fake discounts
Artificial price inflation
Hidden charges
7. Off-Platform Sales

Sellers may not direct buyers to complete purchases outside Bonsai Broker in order to avoid marketplace fees.

8. Seller Ratings

Buyer reviews help maintain marketplace integrity.

Attempts to manipulate ratings may result in account suspension.

9. Account Suspension

Bonsai Broker may remove listings or suspend accounts for violations of these rules.

Prohibited Items

The following may not be listed for sale:

Illegal Items
Stolen property
Counterfeit goods
Illegal wildlife
Illegal plants
Illegal pesticides
Controlled substances
Protected Plants

Plants protected by federal, state, or international law without proper permits.

Examples include:

Certain endangered species
Protected native trees
CITES-regulated species without documentation
Unsafe Products
Hazardous chemicals
Poisonous materials
Explosives
Firearms
Ammunition
Fraudulent Listings

Listings containing:

Misleading descriptions
Fake photographs
Items not owned by the seller
Adult Content

No sexually explicit material.

Hate Content

No products promoting:

Violence
Terrorism
Hate groups
Illegal activity
Shipping Policy
Seller Responsibility

Sellers are responsible for:

Packaging
Shipping
Carrier selection
Tracking numbers
Live Trees

Because Bonsai Broker specializes in live trees, sellers should:

Secure root balls
Protect branches
Protect pots
Prevent moisture loss
Consider seasonal temperatures
Shipping Delays

Bonsai Broker is not responsible for:

Weather delays
Carrier delays
Customs delays
Lost packages
Tracking

Tracking information should be provided whenever available.

International Shipments

Sellers are responsible for complying with all import/export laws.

Damage During Shipping

Damage claims should first be handled between:

Buyer

and

Seller.

Refund Policy
Marketplace Role

Bonsai Broker is a marketplace connecting buyers and sellers.

Bonsai Broker is not the seller.

Seller Refund Policies

Each seller may establish their own refund policy.

That policy must be clearly stated in every listing.

If No Refund Policy Exists

If a seller has not published a refund policy:

All sales are considered final unless required by applicable law.

Refund Requests

Buyers should contact sellers directly.

Chargebacks

Fraudulent chargebacks may result in account suspension.

Marketplace Intervention

Bonsai Broker may review disputes but is not obligated to issue refunds.`;

export const POLICY_BUNDLE_TEXT = `${MARKETPLACE_PROTECTION_TEXT}\n\n---\n\n${COMMUNITY_STANDARDS_TEXT}\n\n---\n\n${DMCA_POLICY_TEXT}\n\n---\n\n${SELLER_RULES_TEXT}`;

export const policyPages = {
  protection: { title: "Marketplace Protection Policy", text: MARKETPLACE_PROTECTION_TEXT },
  community: { title: "Community Standards", text: COMMUNITY_STANDARDS_TEXT },
  dmca: { title: "DMCA / Copyright Policy", text: DMCA_POLICY_TEXT },
  sellers: { title: "Seller Rules", text: SELLER_RULES_TEXT },
};